/**
 * New Relic Instrumentation for Aspect Ratio Detection
 * US1835672: Support for multiple aspect ratios in SRP gallery
 *
 * Collects real-world data on image aspect ratios to inform solution approach
 */

import {
	setNewRelicCustomAttribute,
	addNewRelicPageAction
} from 'ddc-new-relic';
import { calculateAspectRatio } from './aspectRatioDetection';

// Track if we've already sent summary data to avoid duplicates
let hasTrackedSummary = false;
const aspectRatioData = new Map();

/**
 * Track aspect ratio data for a single image
 * @param {HTMLImageElement} imageElement - The loaded image element
 * @param {Object} imageMetadata - Additional image metadata
 * @param {string} imageMetadata.provider - Image provider (ACTUAL_PHOTO, STOCK_PHOTO, etc.)
 * @param {string} imageMetadata.uuid - Vehicle UUID
 * @param {number} imageMetadata.imageIndex - Position in image array (0-based)
 * @param {string} imageMetadata.carouselType - Type of carousel (MEDIA_FOCUSED, IMAGE, etc.)
 */
export const trackImageAspectRatio = (imageElement, imageMetadata = {}) => {
	if (!imageElement) return;

	const {
		provider = 'UNKNOWN',
		uuid = 'UNKNOWN',
		imageIndex = 0,
		carouselType = 'UNKNOWN'
	} = imageMetadata;

	const aspectRatioInfo = calculateAspectRatio(imageElement);

	// Skip if we couldn't calculate (loading error, blank.gif, etc.)
	if (!aspectRatioInfo.ratio) return;

	const { ratio, width, height, category, cssRatio } = aspectRatioInfo;

	// Store data for summary
	const key = `${uuid}-${imageIndex}`;
	if (!aspectRatioData.has(key)) {
		aspectRatioData.set(key, {
			ratio,
			category,
			provider,
			carouselType,
			imageIndex,
			dimensions: `${width}x${height}`
		});
	}

	// Send individual image data as page action (for detailed analysis)
	addNewRelicPageAction('SRP_IMAGE_ASPECT_RATIO', {
		aspectRatio: ratio,
		aspectRatioCategory: category,
		aspectRatioCss: cssRatio,
		imageProvider: provider,
		imageIndex,
		carouselType,
		imageDimensions: `${width}x${height}`,
		vehicleUuid: uuid
	});
};

/**
 * Track summary statistics for all images on the page
 * Call this after all images have loaded or on page unload
 */
export const trackAspectRatioSummary = () => {
	if (hasTrackedSummary || aspectRatioData.size === 0) return;

	const categoryCounts = {};
	const providerCounts = {};
	const carouselTypeCounts = {};
	let totalImages = 0;
	let standardCount = 0;
	let widescreenCount = 0;
	let customCount = 0;

	// Aggregate data
	aspectRatioData.forEach(({ category, provider, carouselType }) => {
		totalImages += 1;

		// Count by category
		categoryCounts[category] = (categoryCounts[category] || 0) + 1;

		// Count by provider
		providerCounts[provider] = (providerCounts[provider] || 0) + 1;

		// Count by carousel type
		carouselTypeCounts[carouselType] =
			(carouselTypeCounts[carouselType] || 0) + 1;

		// Track key categories for quick analysis
		if (category === 'STANDARD') standardCount += 1;
		if (category === 'WIDESCREEN') widescreenCount += 1;
		if (category.startsWith('CUSTOM')) customCount += 1;
	});

	// Calculate percentages
	const standardPct = ((standardCount / totalImages) * 100).toFixed(1);
	const widescreenPct = ((widescreenCount / totalImages) * 100).toFixed(1);
	const customPct = ((customCount / totalImages) * 100).toFixed(1);

	// Set custom attributes for dashboard metrics
	setNewRelicCustomAttribute('srpTotalImages', totalImages);
	setNewRelicCustomAttribute('srpStandardRatioCount', standardCount);
	setNewRelicCustomAttribute('srpWidescreenRatioCount', widescreenCount);
	setNewRelicCustomAttribute('srpCustomRatioCount', customCount);
	setNewRelicCustomAttribute('srpStandardRatioPct', parseFloat(standardPct));
	setNewRelicCustomAttribute(
		'srpWidescreenRatioPct',
		parseFloat(widescreenPct)
	);
	setNewRelicCustomAttribute('srpCustomRatioPct', parseFloat(customPct));

	// Send detailed page action with all category breakdown
	addNewRelicPageAction('SRP_ASPECT_RATIO_SUMMARY', {
		totalImages,
		categoryCounts: JSON.stringify(categoryCounts),
		providerCounts: JSON.stringify(providerCounts),
		carouselTypeCounts: JSON.stringify(carouselTypeCounts),
		standardPct: parseFloat(standardPct),
		widescreenPct: parseFloat(widescreenPct),
		customPct: parseFloat(customPct),
		hasMultipleAspectRatios: Object.keys(categoryCounts).length > 1
	});

	hasTrackedSummary = true;
};

/**
 * Reset tracking state (useful for testing or page navigation)
 */
export const resetAspectRatioTracking = () => {
	hasTrackedSummary = false;
	aspectRatioData.clear();
};

/**
 * Get current tracking data (for debugging)
 * @returns {Array} Array of tracked aspect ratio data
 */
export const getTrackedAspectRatios = () => {
	return Array.from(aspectRatioData.values());
};

export default {
	trackImageAspectRatio,
	trackAspectRatioSummary,
	resetAspectRatioTracking,
	getTrackedAspectRatios
};
